import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Form } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import { FETCH_FORMS_LIST } from 'actions/formActions';
import { FaEye, FaSearch, FaTimes, FaChargingStation, FaCheckCircle, FaClock, FaFileAlt, FaTrash } from 'react-icons/fa';
import { deleteStation } from 'api/formApi';
import { toast } from 'react-toastify';
import { cookie } from 'utils/cookies';

const ShimmerLoader = () => {
    return (
        <div className="glass-panel shadow-sm p-4" style={{ overflow: 'hidden', background: '#fff', borderRadius: '16px' }}>
            <style dangerouslySetInnerHTML={{__html: `
                @keyframes shimmer {
                    0% {
                        background-position: -468px 0;
                    }
                    100% {
                        background-position: 468px 0;
                    }
                }
                .shimmer-element {
                    animation: shimmer 1.5s linear infinite;
                    background: linear-gradient(to right, #f1f5f9 8%, #e2e8f0 18%, #f1f5f9 33%);
                    background-size: 800px 104px;
                    position: relative;
                }
            `}} />
            <div className="table-responsive">
                <table className="premium-table" style={{ pointerEvents: 'none', width: '100%' }}>
                    <thead>
                        <tr>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '120px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '150px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '100px', borderRadius: '4px' }}></div></th>
                            <th><div className="shimmer-element" style={{ height: '16px', width: '80px', borderRadius: '4px' }}></div></th>
                            <th style={{ textAlign: 'center' }}><div className="shimmer-element m-auto" style={{ height: '16px', width: '80px', borderRadius: '4px' }}></div></th>
                        </tr>
                    </thead>
                    <tbody>
                        {[1, 2, 3, 4, 5].map((i) => (
                            <tr key={i}>
                                <td className="py-3">
                                    <div className="d-flex align-items-center gap-2">
                                        <div className="shimmer-element" style={{ width: '36px', height: '36px', borderRadius: '10px', flexShrink: 0 }}></div>
                                        <div className="shimmer-element" style={{ height: '16px', width: '120px', borderRadius: '4px' }}></div>
                                    </div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '14px', width: '180px', borderRadius: '4px' }}></div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '14px', width: '100px', borderRadius: '4px' }}></div>
                                </td>
                                <td className="py-3">
                                    <div className="shimmer-element" style={{ height: '24px', width: '90px', borderRadius: '20px' }}></div>
                                </td>
                                <td className="py-3" style={{ textAlign: 'center' }}>
                                    <div className="d-flex gap-2 justify-content-center">
                                        <div className="shimmer-element" style={{ width: '32px', height: '32px', borderRadius: '8px' }}></div>
                                        <div className="shimmer-element" style={{ width: '32px', height: '32px', borderRadius: '8px' }}></div>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const FormList = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { loading, allFormsList } = useSelector((state) => state.checklist);
    const [search, setSearch] = useState('');
    const [userFilter, setUserFilter] = useState('');
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);
    const userRole = cookie.get('user-role');

    useEffect(() => {
        dispatch({ type: FETCH_FORMS_LIST });
    }, [dispatch]);

    const handleDeleteStation = async (stationId) => {
        if (window.confirm(`Are you sure you want to delete station "${stationId}" and all its associated forms? This action cannot be undone.`)) {
            try {
                const response = await deleteStation(stationId);
                if (response.status === 200) {
                    toast.success('Station and associated forms deleted successfully');
                    dispatch({ type: FETCH_FORMS_LIST }); // Refresh list
                } else {
                    toast.error(response.message || 'Failed to delete station');
                }
            } catch (error) {
                toast.error(error.message || 'An error occurred while deleting the station');
            }
        }
    };

    // Group forms by stationId
    const groupedByStation = useMemo(() => {
        return (allFormsList || []).filter(f => {
            if (userRole === 'quality') {
                const s = (f.status || '').toLowerCase();
                const s1 = (f.l1?.status || '').toLowerCase();
                const s2 = (f.l2?.status || '').toLowerCase();
                return s === 'quality' || s1 === 'quality' || s2 === 'quality';
            }
            return true;
        }).reduce((acc, form) => {
            if (!form.stationId || typeof form.stationId !== 'string') return acc;
            if (!acc[form.stationId]) acc[form.stationId] = [];
            acc[form.stationId].push(form);
            return acc;
        }, {});
    }, [allFormsList]);

    const uniqueUsers = useMemo(() => {
        return Array.from(new Set((allFormsList || []).map(f => f.userName).filter(Boolean))).sort();
    }, [allFormsList]);

    // Build filtered and structured station data
    const filteredList = useMemo(() => {
        return Object.entries(groupedByStation)
            .filter(([, forms]) => forms.length > 0)
            .filter(([stationId, forms]) => {
                const stationMatch = stationId.toLowerCase().includes(search.toLowerCase());
                const userMatchInSearch = forms[0].userName?.toLowerCase().includes(search.toLowerCase());
                const projectMatchInSearch = forms[0].project_name?.toLowerCase().includes(search.toLowerCase());
                
                const matchesSearch = stationMatch || userMatchInSearch || projectMatchInSearch;
                
                const matchesUserFilter = !userFilter || forms[0].userName === userFilter;
                
                return matchesSearch && matchesUserFilter;
            })
            .map(([stationId, forms]) => {
                const totalForms = forms.length;
                const approvedForms = forms.filter((f) => (f.status || '').toLowerCase() === 'approved').length;
                const draftForms = forms.filter((f) => (f.status || '').toLowerCase() === 'draft').length;
                const pendingForms = totalForms - approvedForms - draftForms;

                const { project_name, userName } = forms[0];

                return {
                    stationId,
                    project_name: project_name || 'N/A',
                    userName: userName || 'N/A',
                    status: approvedForms === totalForms && totalForms > 0 ? 'completed' : 'in-progress',
                    formCount: totalForms,
                    approvedCount: approvedForms,
                    draftCount: draftForms,
                    pendingCount: pendingForms,
                };
            })
            .sort((a, b) => a.stationId.localeCompare(b.stationId));
    }, [groupedByStation, search, userFilter]);

    // Summary stats
    const totalStations = filteredList.length;
    const totalApproved = filteredList.reduce((s, f) => s + f.approvedCount, 0);
    const totalPending = filteredList.reduce((s, f) => s + f.pendingCount, 0);
    const totalDraft = filteredList.reduce((s, f) => s + f.draftCount, 0);

    const statCards = [
        { label: 'Total Stations', value: totalStations, icon: <FaChargingStation size={20} />, iconColor: '#475569', iconBg: '#f1f5f9' },
        { label: 'Approved', value: totalApproved, icon: <FaCheckCircle size={20} />, iconColor: '#16a34a', iconBg: '#dcfce7' },
        { label: 'Pending', value: totalPending, icon: <FaClock size={20} />, iconColor: '#ea580c', iconBg: '#ffedd5' },
        { label: 'Draft', value: totalDraft, icon: <FaFileAlt size={20} />, iconColor: '#2563eb', iconBg: '#dbeafe' },
    ];

    return (
        <div className="page-body checklist-container">
            <div className="container-xl">
                {/* Header */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                    <div>
                        <h2 className="page-title m-0" style={{ fontWeight: 800, letterSpacing: '-0.5px' }}>
                            Checklist Overview
                        </h2>
                        <p className="text-muted small mb-0 mt-1">Manage and monitor all station checklists</p>
                    </div>
                </div>

                {/* Stat Cards */}
                <div className="row g-3 mb-4">
                    {statCards.map((card) => (
                        <div className="col-6 col-lg-3" key={card.label}>
                            <div style={{
                                background: '#ffffff',
                                border: '1px solid #e2e8f0',
                                borderRadius: '16px',
                                padding: '20px 22px',
                                boxShadow: '0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px 0 rgba(0,0,0,0.06)',
                                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                                cursor: 'default',
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.transform = 'translateY(-3px)';
                                e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(0,0,0,0.05), 0 4px 6px -4px rgba(0,0,0,0.05)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 1px 3px 0 rgba(0,0,0,0.05), 0 1px 2px 0 rgba(0,0,0,0.06)';
                            }}
                            >
                                <div className="d-flex align-items-center justify-content-between mb-3">
                                    <span style={{ color: '#64748b', fontSize: '13px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{card.label}</span>
                                    <span style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        width: '38px',
                                        height: '38px',
                                        borderRadius: '10px',
                                        background: card.iconBg,
                                        color: card.iconColor,
                                    }}>{card.icon}</span>
                                </div>
                                <div style={{ fontSize: '28px', fontWeight: 800, lineHeight: 1, color: '#0f172a' }}>{card.value}</div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Search */}
                <div className="premium-filter-section mb-4">
                    <div className={`filter-group search-wrapper ${search || isSearchExpanded ? 'expanded' : ''}`}>
                        <label htmlFor="station-search">Search Stations</label>
                        <div className="position-relative">
                            <Form.Control
                                id="station-search"
                                type="text"
                                placeholder="Search by Station, Filer or Project..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                onFocus={() => setIsSearchExpanded(true)}
                                onBlur={() => !search && setIsSearchExpanded(false)}
                            />
                            <FaSearch className="search-icon" />
                        </div>
                    </div>
                    <div className="filter-group select-wrapper">
                        <label htmlFor="user-filter">Filter by Filer</label>
                        <Form.Select
                            id="user-filter"
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            style={{ borderRadius: '12px', border: '1px solid #e2e8f0', height: '42px', paddingLeft: '15px' }}
                        >
                            <option value="">All Filers</option>
                            {uniqueUsers.map(u => (
                                <option key={u} value={u}>{u}</option>
                            ))}
                        </Form.Select>
                    </div>
                    { (search || userFilter) && (
                        <div className="filter-group flex-grow-0">
                            <span className="filter-label-spacer" aria-hidden="true">&nbsp;</span>
                            <button id="clear-search-btn" className="btn-clear" onClick={() => { setSearch(''); setUserFilter(''); }} title="Clear Filters">
                                <FaTimes /> Clear
                            </button>
                        </div>
                    )}
                </div>

                {/* Table */}
                {loading ? (
                    <ShimmerLoader />
                ) : (
                    <div className="table-responsive">
                        <table className="premium-table">
                            <thead>
                                <tr>
                                    <th>Station</th>
                                    <th>Project</th>
                                    <th>Created By</th>
                                    <th>Status</th>
                                    <th style={{ textAlign: 'center' }}>Action</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredList.map((form) => (
                                    <tr 
                                        key={form.stationId}
                                        onClick={() => navigate(`/formList/${form.stationId}`)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <td>
                                            <div className="d-flex align-items-center gap-2">
                                                <div style={{
                                                    width: '36px', height: '36px', borderRadius: '10px',
                                                    background: '#f1f5f9',
                                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                    flexShrink: 0,
                                                }}>
                                                    <FaChargingStation color="#475569" size={16} />
                                                </div>
                                                <div>
                                                    <span
                                                        className="fw-bold"
                                                        role="button"
                                                        tabIndex="0"
                                                        style={{ color: '#1e293b', fontSize: '14px' }}
                                                    >
                                                        {form.stationId}
                                                    </span>
                                                </div>
                                            </div>
                                        </td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.project_name}</span></td>
                                        <td><span className="text-muted" style={{ fontSize: '13px' }}>{form.userName}</span></td>
                                        <td>
                                            <span className={`status-pill status-${form.status}`}>
                                                {form.status === 'completed' ? 'Completed' : 'In Progress'}
                                            </span>
                                        </td>
                                        <td style={{ textAlign: 'center' }}>
                                            <div className="action-btn-group" style={{ justifyContent: 'center', gap: '8px' }}>
                                                <button
                                                    className="btn-premium"
                                                    title="View Station Forms"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        navigate(`/formList/${form.stationId}`);
                                                    }}
                                                >
                                                    <FaEye />
                                                </button>
                                                {(userRole === 'admin' || userRole === 'manager') && (
                                                    <button
                                                        className="btn-premium btn-danger-soft"
                                                        title="Delete Station"
                                                        onClick={(e) => {
                                                            e.stopPropagation();
                                                            handleDeleteStation(form.stationId);
                                                        }}
                                                        style={{ background: '#fee2e2', color: '#ef4444' }}
                                                    >
                                                        <FaTrash />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredList.length === 0 && (
                                    <tr>
                                        <td colSpan="9" className="text-center py-5">
                                            <div className="d-flex flex-column align-items-center">
                                                <FaChargingStation size={48} className="text-muted mb-3" style={{ opacity: 0.3 }} />
                                                <p className="text-muted fw-bold mb-1">No stations found</p>
                                                <p className="text-muted small">Try adjusting your search criteria</p>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
};

export default FormList;